import { useState } from "react";
import { Link } from "react-router-dom";
import { Check, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { errMsg, fmtDate } from "@/lib/api";
import { reviewChanged } from "@/components/AppShell";
import { SourceImage } from "@/components/SourceImage";
import { t } from "@/i18n";
import { Tt } from "@/components/Tt";

export function PendingBubble({ s, api, onDone }: { s: any; api: any; onDone: (id: string, status: string, reason?: string) => void }) {
  const [busy, setBusy] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState("");
  const act = async (path: string, status: string, msg: string) => {
    setBusy(true);
    try {
      await api.post(`/sops/${s.id}/${path}`, path === "reject" ? { reason } : { note: "Approved from chat" });
      toast.success(msg);
      onDone(s.id, status, reason);
      reviewChanged();
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  const status = s.decision === "approved" ? "published" : s.decision === "rejected" ? "rejected" : s.status;
  const done = status === "published" || status === "rejected";
  return (
    <div className="flex flex-col items-start" data-testid={`pending-bubble-${s.id}`}>
      <div className="bubble-in max-w-[85%] stack-sm">
        {s.cover_source_id && <SourceImage sourceId={s.cover_source_id} className="w-full max-w-xs h-40 rounded-xl" testid={`pending-cover-${s.id}`} />}
        <p className="text-xs faint">{t(s.resubmit_of ? "{name} resubmitted OI" : "{name} submitted OI", { name: s.submitted_by_name || t("Staff") })}</p>
        {s.resubmit_of && s.prev_reject_reason && !done && <p className="text-xs muted" data-testid={`pending-prev-note-${s.id}`}>{t("Your earlier note")}: “{s.prev_reject_reason}”</p>}
        <Link to={`/sops/${s.id}`} className="font-semibold hover:underline" data-testid={`pending-open-${s.id}`}><Tt text={s.title} /></Link>
        {s.draft?.summary && !done && <p className="text-sm muted">{s.draft.summary}</p>}
        {status === "processing" && <p className="text-xs faint flex items-center gap-1"><Loader2 size={12} className="animate-spin" /> {t("Drafting…")}</p>}
        {status === "draft" && !rejecting && (
          <div className="flex gap-2 pt-1">
            <button className="btn btn-sm !bg-[var(--status-ok)] text-white flex-1" disabled={busy} onClick={() => act("publish", "published", t("Approved and published"))} data-testid={`pending-approve-${s.id}`}><Check size={15} /> {t("Approve")}</button>
            <button className="btn btn-sm btn-ghost flex-1 !text-[var(--status-error)]" disabled={busy} onClick={() => setRejecting(true)} data-testid={`pending-reject-${s.id}`}><X size={15} /> {t("Reject")}</button>
          </div>
        )}
        {status === "draft" && rejecting && (
          <form className="flex gap-2 pt-1" onSubmit={(e) => { e.preventDefault(); act("reject", "rejected", t("Sent back with your note")); }}>
            <input autoFocus className="field !py-2 text-sm" value={reason} onChange={(e) => setReason(e.target.value)} placeholder={t("Quick reason, e.g. add milk amount")} data-testid={`pending-reject-reason-${s.id}`} />
            <button className="btn btn-sm !bg-[var(--status-error)] text-white shrink-0" disabled={busy || !reason.trim()} data-testid={`pending-reject-confirm-${s.id}`}>{t("Send")}</button>
          </form>
        )}
        {done && (
          <div data-testid={`pending-result-${s.id}`}>
            <p className={`text-sm font-semibold ${status === "published" ? "text-[var(--status-ok)]" : "text-[var(--status-error)]"}`}>
              {status === "published" ? `✓ ${t("Approved")}` : t("Not approved")}{s.decided_by ? ` · ${s.decided_by}` : ""}{s.decided_at ? ` · ${fmtDate(s.decided_at)}` : ""}
            </p>
            {status === "rejected" && s.reject_reason && <p className="text-xs muted mt-0.5">“{s.reject_reason}”</p>}
          </div>
        )}
      </div>
    </div>
  );
}
